import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";

interface DashboardHeaderProps {
  onPostJob: () => void;
  onNavigate: (section: string) => void;
  onMobileMenuToggle?: () => void;
}

export default function DashboardHeader({ onPostJob, onNavigate, onMobileMenuToggle }: DashboardHeaderProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [searchFocused, setSearchFocused] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotificationOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const companyName = user?.company_name || "Doanh nghiệp của bạn";
  const companyInitial = companyName.charAt(0).toUpperCase();
  const isBanned = user?.status === "banned";

  const handleLogout = async () => {
    await logout();
    navigate("/doanh-nghiep/dang-nhap", { replace: true });
  };

  return (
    <header className="h-16 flex items-center justify-between px-4 md:px-6 bg-background-50 border-b border-background-200/70 shrink-0">
      {/* Mobile hamburger */}
      <button
        onClick={onMobileMenuToggle}
        className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl hover:bg-background-100 transition-colors cursor-pointer mr-2 shrink-0"
      >
        <i className="ri-menu-line text-xl text-foreground-700"></i>
      </button>

      <div className="flex-1 max-w-lg hidden sm:block">
        <div
          className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all duration-200 ${
            searchFocused
              ? "border-primary-300 bg-background-50 shadow-[0_0_0_3px_var(--primary-100)]"
              : "border-background-200 bg-background-100"
          }`}
        >
          <div className="w-5 h-5 flex items-center justify-center shrink-0">
            <i className={`ri-search-line text-base ${searchFocused ? "text-primary-500" : "text-foreground-400"}`}></i>
          </div>
          <input
            type="text"
            placeholder="Tìm kiếm tin tuyển dụng, ứng viên..."
            className="flex-1 bg-transparent border-none outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
          <kbd className="hidden md:inline-flex items-center gap-0.5 px-2 py-0.5 text-[11px] text-foreground-400 bg-background-200/70 rounded-md font-mono">
            <span>⌘</span><span>K</span>
          </kbd>
        </div>
      </div>

      <div className="flex items-center gap-2 ml-4">
        <button
          onClick={onPostJob}
          disabled={isBanned}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors whitespace-nowrap ${
            isBanned
              ? "bg-background-200 text-foreground-400 cursor-not-allowed"
              : "bg-primary-500 hover:bg-primary-600 text-background-50 cursor-pointer"
          }`}
          title={isBanned ? "Tài khoản doanh nghiệp đang bị khóa" : "Đăng tin tuyển dụng mới"}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line text-base"></i>
          </div>
          <span className="hidden sm:inline">Đăng tin</span>
        </button>

        <div className="relative" ref={notifRef}>
          <button
            onClick={() => setNotificationOpen(!notificationOpen)}
            className="relative w-10 h-10 flex items-center justify-center rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-notification-3-line text-xl text-foreground-600"></i>
            </div>
            <span className="absolute top-2 right-2 w-2 h-2 bg-accent-500 rounded-full ring-2 ring-background-50"></span>
          </button>

          {notificationOpen && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-background-50 rounded-xl border border-background-200/70 overflow-hidden z-50">
              <div className="flex items-center justify-between px-4 py-3 border-b border-background-100">
                <span className="font-semibold text-sm text-foreground-900">Thông báo</span>
                <span className="bg-accent-100 text-accent-700 text-[11px] font-semibold px-2 py-0.5 rounded-full">3 mới</span>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {[
                  { id: 1, title: "Ứng viên mới", desc: "Nguyễn Văn Minh ứng tuyển Công nhân sản xuất", time: "2 phút trước", unread: true },
                  { id: 2, title: "Tin sắp hết hạn", desc: "Tài xế giao hàng nội thành còn 17 ngày", time: "1 giờ trước", unread: true },
                  { id: 3, title: "Lượt xem tăng", desc: "Tin bán hàng siêu thị tăng 45% lượt xem", time: "3 giờ trước", unread: true },
                  { id: 4, title: "Hệ thống", desc: "Đã tự động làm mới 5 tin tuyển dụng", time: "2 ngày trước", unread: false },
                ].map((notif) => (
                  <div
                    key={notif.id}
                    className={`px-4 py-3 border-b border-background-100 last:border-b-0 hover:bg-background-50 transition-colors cursor-pointer ${
                      notif.unread ? "bg-primary-50/50" : ""
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${notif.unread ? "bg-primary-500" : "bg-transparent"}`}></div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-900 truncate">{notif.title}</p>
                        <p className="text-xs text-foreground-600 mt-0.5 line-clamp-2">{notif.desc}</p>
                        <p className="text-[11px] text-foreground-400 mt-1.5">{notif.time}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-4 py-2.5 border-t border-background-100">
                <button className="w-full text-center text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
                  Xem tất cả thông báo
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
          >
            <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
              <span className="text-sm font-semibold text-accent-700">{companyInitial}</span>
            </div>
            <div className="hidden sm:block text-left min-w-0">
              <p className="text-sm font-semibold text-foreground-900 truncate max-w-[140px]">{companyName}</p>
              <p className="text-[11px] text-foreground-500">Quản trị viên</p>
            </div>
            <div className="w-4 h-4 flex items-center justify-center shrink-0 hidden sm:flex">
              <i className="ri-arrow-down-s-line text-foreground-400 text-sm"></i>
            </div>
          </button>

          {userMenuOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-background-50 rounded-xl border border-background-200/70 overflow-hidden z-50">
              <div className="px-4 py-3 border-b border-background-100">
                <p className="text-sm font-semibold text-foreground-900 truncate">{companyName}</p>
                <p className="text-xs text-foreground-500 truncate">{user?.email || "Chưa có email"}</p>
              </div>
              <div className="py-1">
                <button
                  onClick={() => { onNavigate("profile"); setUserMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center shrink-0"><i className="ri-building-4-line text-foreground-500"></i></div>
                  <span>Hồ sơ doanh nghiệp</span>
                </button>
                <button
                  onClick={() => { onNavigate("account"); setUserMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center shrink-0"><i className="ri-user-settings-line text-foreground-500"></i></div>
                  <span>Quản lý tài khoản</span>
                </button>
                <div className="border-t border-background-100 my-1"></div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center shrink-0"><i className="ri-logout-box-line text-red-500"></i></div>
                  <span>Đăng xuất</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}